<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { Toaster } from 'vue-sonner';
import { syncRuntimeAccountTheme, useTheme } from '@/lib/theme';
import { useAuthStore } from '@/stores/auth';
import { useAccountStore } from '@/stores/account';

const colorMode = useTheme();

const auth = useAuthStore();
const account = useAccountStore();

function primaryFromActiveSettings(): string | null {
  const s = auth.activeSettings;
  if (!s || typeof s !== 'object') return null;
  const theme = (s as Record<string, unknown>).theme;
  if (!theme || typeof theme !== 'object' || theme === null) return null;
  const p = (theme as Record<string, unknown>).primary;
  return typeof p === 'string' && /^#[0-9A-Fa-f]{6}$/.test(p) ? p : null;
}

const accountPrimaryHex = computed(() => {
  if (account.showingAllAccounts) return null;
  return (
    account.activeAccount?.primaryColor ??
    primaryFromActiveSettings() ??
    auth.activeAccount?.primaryColor ??
    null
  );
});

const colorScheme = computed((): 'light' | 'dark' => (colorMode.value === 'dark' ? 'dark' : 'light'));

watch(
  () => ({ hex: accountPrimaryHex.value, scheme: colorScheme.value }),
  ({ hex, scheme }) => {
    syncRuntimeAccountTheme({ primaryHex: hex, scheme });
  },
  { immediate: true },
);

onMounted(async () => {
  if (!auth.ready) await auth.refresh();
  if (!auth.isSuperAdmin && !account.activeAccount && auth.activeAccount) {
    await account.setActive(auth.activeAccount);
  }
});

watch(
  () => auth.activeAccount,
  (next) => {
    if (!auth.isSuperAdmin && next && !account.activeAccount) {
      void account.setActive(next);
    }
  },
);
</script>

<template>
  <RouterView />
  <Toaster position="bottom-right" rich-colors />
</template>
